import { Children, isValidElement, type HTMLAttributes, type ReactElement, type ReactNode, type Ref } from "react";

import { cn } from "../../lib/utils";
import { Button, ButtonGroup } from "../../atoms/Button";
import { DropdownMenu } from "../../atoms/DropdownMenu";
import { Tooltip } from "../../atoms/Tooltip";

export interface EmptyStateActionsProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}

interface ActionWrapperProps {
  children?: ReactNode;
  asChild?: boolean;
}

const TRIGGER_BY_OVERLAY_ROOT = new Map<unknown, unknown>([
  [DropdownMenu, DropdownMenu.Trigger],
  [Tooltip, Tooltip.Trigger],
]);

const isButton = (node: ReactNode) => isValidElement(node) && (node.type === Button || node.type === ButtonGroup);

// Without `asChild` the overlay's Trigger renders its own <button> around ours (nested buttons).
const isButtonAction = (node: ReactElement<ActionWrapperProps>) => {
  if (isButton(node)) return true;

  const triggerType = TRIGGER_BY_OVERLAY_ROOT.get(node.type);
  if (!triggerType) return false;

  const trigger = Children.toArray(node.props.children).find(
    (child): child is ReactElement<ActionWrapperProps> =>
      isValidElement<ActionWrapperProps>(child) && child.type === triggerType
  );
  return trigger?.props.asChild === true && isButton(trigger.props.children);
};

// Row of primary/secondary actions -- children must be `Button` (or `Button.Group`), optionally as
// the `asChild` Trigger of a DropdownMenu/Tooltip, so an EmptyState's actions stay
// visually/behaviorally consistent with every other action surface.
export const EmptyStateActions = ({ className, ref, children, ...props }: EmptyStateActionsProps) => {
  if (process.env.NODE_ENV !== "production") {
    Children.forEach(children, (child) => {
      if (isValidElement<ActionWrapperProps>(child) && !isButtonAction(child)) {
        console.error("EmptyState.Actions only accepts Button or Button.Group children.");
      }
    });
  }

  return (
    <div
      ref={ref}
      data-slot="empty-state-actions"
      className={cn("flex flex-wrap items-center justify-center gap-2", className)}
      {...props}
    >
      {children}
    </div>
  );
};
